/**
 * Per-row kebab for the admin Users table. Reuses the shared RowActionsMenu
 * shell (Edit + destructive Delete, auto-separator) and owns its dialog
 * state, like ClientRowActions. Replaces the GridActionsCellItem-based
 * EditUserAction/DeleteUserAction; the self-delete guard carries over.
 * Both dialogs close themselves on success, so the row unmounting on the
 * following refetch loses nothing.
 */
import type { IUser } from "@/api";
import { UserFormDialog } from "@/components/admin/dialogs/new-user-dialog/UserFormDialog";
import {
    RowActionsMenu,
    type RowAction,
} from "@/components/shared/RowActionsMenu";
import { useAuth } from "@/contexts/auth/useAuth";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";

import { DeleteUsersDialog } from "./dialogs/DeleteUsersDialog";

export function UserRowActions({ user }: { user: IUser }) {
    const { user: currentUser } = useAuth();
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const actions: RowAction[] = [
        {
            key: "edit",
            label: "Edit",
            icon: <Pencil className="size-4" />,
            onSelect: () => setEditOpen(true),
        },
        {
            key: "delete",
            label: "Delete",
            icon: <Trash2 className="size-4" />,
            variant: "destructive",
            disabled: currentUser?.id === user.id,
            onSelect: () => setDeleteOpen(true),
        },
    ];

    return (
        <>
            <RowActionsMenu actions={actions} ariaLabel="Open actions menu" />
            <UserFormDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                user={user}
            />
            <DeleteUsersDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                userIds={[user.id]}
            />
        </>
    );
}
